/* eslint-disable react/jsx-no-undef */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Zap,
  Thermometer,
  Wind,
  Calendar,
  Search,
  X,
  Download,
  ChevronDown,
  BarChart3,
  Table,
  Loader2,
  AlertCircle,
  CheckSquare,
  Square,
  RefreshCw,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ultronTuringApi } from '../services/ultronTuringApi';
import { getImportedDevices } from '../services/userPreferencesApi';
import { logger } from '../utils/logger';
import { getTimezoneOffset } from '../utils/timezone';

interface Device {
  sn: string;
  deviceId?: string;
  name?: string;
  displayName?: string;
  nickname?: string | null;
  type?: string;
  deviceType?: string;
  groupId?: string;
  groupName?: string;
  traits?: string[];
  attributes?: {
    sensorProfile?: {
      watt?: number;
      watt_hour?: number;
      voltage?: number;
      current?: number;
      energy?: number;
      power?: number;
      wattHour?: number;
      kwh?: number;
      kWh?: number;
      wh?: number;
      temp?: number;
      rh?: number;
      temperature?: number;
      humidity?: number;
      tempC?: number;
      humidityPercent?: number;
      co2?: number;
      tvoc?: number;
      pm25?: number;
      pm10?: number;
      airQuality?: number;
      [key: string]: any;
    };
    watt?: number;
    watt_hour?: number;
    voltage?: number;
    current?: number;
    energy?: number;
    power?: number;
    wattHour?: number;
    kwh?: number;
    kWh?: number;
    wh?: number;
    temperature?: number;
    humidity?: number;
    temp?: number;
    rh?: number;
    tempC?: number;
    humidityPercent?: number;
    co2?: number;
    tvoc?: number;
    pm25?: number;
    pm10?: number;
    airQuality?: number;
    [key: string]: any;
  };
  capabilities?: Array<string | { type?: string; [key: string]: any }>;
  watt?: number;
  wattHour?: number;
  voltage?: number;
  current?: number;
  energy?: number;
  power?: number;
  kwh?: number;
  kWh?: number;
  wh?: number;
  temperature?: number;
  humidity?: number;
  temp?: number;
  rh?: number;
  co2?: number;
  tvoc?: number;
  pm25?: number;
  pm10?: number;
  airQuality?: number;
}

interface ChartDataPoint {
  time: string;
  displayTime: string;  // 格式化后的时间，用于图表显示
  // 電力設備
  avgPower?: number;
  energy?: number;
  minPower?: number;
  maxPower?: number;
  // 溫溼度
  temperature?: number;
  humidity?: number;
  // 空氣品質
  co2?: number;
  tvoc?: number;
  pm25?: number;
  pm10?: number;
}

type DeviceType = '電力設備' | '溫溼度' | '空氣品質';
type Period = 'week' | 'month' | '6months' | 'year';
type ViewTab = 'chart' | 'table';

// 将 IANA 时区转换为 UTC offset 格式（如 '+8:00'）
function timezoneToUtcOffset(timezone: string): string {
  try {
    // 使用 getTimezoneOffset 获取偏移量（分钟）
    const offsetMinutes = getTimezoneOffset(timezone);
    const hours = Math.floor(Math.abs(offsetMinutes) / 60);
    const minutes = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const result = `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    logger.log('🕐 Timezone conversion:', { timezone, offsetMinutes, result });
    return result;
  } catch (error) {
    logger.error('Error converting timezone to UTC offset:', error);
    return '+8:00'; // 默认台北时间
  }
}


// 格式化數字：加上千分號並保留兩位小數
const formatNumber = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) return '-';
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// 独立的图表组件，用 React.memo 包装防止不必要的重新渲染
interface ReportChartProps {
  data: ChartDataPoint[];
  deviceType: DeviceType;
  showAvgPower: boolean;
  showEnergy: boolean;
  showMinPower: boolean;
  showPeakPower: boolean;
  onToggleEnergy: () => void;
  onTogglePeakPower: () => void;
  onToggleAvgPower: () => void;
  onToggleMinPower: () => void;
}

// ReportChart component - defined outside to prevent recreation on each render
function ReportChartComponent(props: ReportChartProps) {
  const { 
    data, 
    deviceType,
    showAvgPower, 
    showEnergy, 
    showMinPower, 
    showPeakPower,
    onToggleEnergy,
    onTogglePeakPower,
    onToggleAvgPower,
    onToggleMinPower
  } = props;
  
  // Calculate temperature and humidity ranges for chart domain
  const tempValues = data.map(d => d.temperature).filter((v): v is number => typeof v === 'number' && v > -50 && v < 100);
  const tempMin = tempValues.length > 0 ? Math.min(...tempValues) : undefined;
  const tempMax = tempValues.length > 0 ? Math.max(...tempValues) : undefined;
  
  // 根據數據點數量動態調整 x 軸標籤間隔
  // 由於 tooltip 會顯示完整時間，所以標籤可以更稀疏
  const getXAxisInterval = () => {
    if (data.length <= 12) return 0;   // 12 個點以內顯示所有標籤
    if (data.length <= 24) return 1;  // 24 個點以內每隔一個顯示
    if (data.length <= 48) return 3;  // 48 個點以內每隔三個顯示
    if (data.length <= 168) return 6; // 168 個點以內每隔六個顯示（約每 6 小時一個）
    return 'preserveStartEnd';  // 更多數據點只顯示首尾
  };
  
  if (data.length === 0) {
    return null;
  }

  // Calculate temperature domain for 溫溼度 device type
  const tempDomain = deviceType === '溫溼度' && tempMin !== undefined && tempMax !== undefined
    ? [Math.max(-50, tempMin - 2), Math.min(100, tempMax + 2)] as [number, number]
    : undefined;
  

  // Custom Tooltip Content - 時間用白色，用電量用粗體，數據加上千分號
  const renderCustomTooltip = (props: any) => {
    const { active, payload, label } = props;
    if (!active || !payload || !payload.length) return null;

    return (
      <div style={{
        backgroundColor: '#1f2937',
        border: '1px solid #374151',
        borderRadius: '8px',
        padding: '12px'
      }}>
        <p style={{ color: '#ffffff', margin: '0 0 8px 0', fontSize: '20px', fontWeight: '500' }}>
          {label}
        </p>
        {payload
          .filter((entry: any) => {
            // 過濾掉 Area 組件：沒有 name 或 name 為空字符串或 name 為 "energy"
            if (!entry.name || entry.name === '' || entry.name === 'energy') return false;
            // 過濾掉 dataKey 為 'energy' 但 name 不是 '用電量 (kWh)' 的條目
            if (entry.dataKey === 'energy' && entry.name !== '用電量 (kWh)') return false;
            return true;
          })
          .map((entry: any, index: number) => {
            const isEnergy = entry.name === '用電量 (kWh)';
            return (
              <p 
                key={index} 
                style={{ 
                  color: entry.color, 
                  margin: '4px 0', 
                  fontSize: '18px',
                  fontWeight: isEnergy ? 'bold' : 'normal'
                }}
              >
                {`${entry.name} : ${typeof entry.value === 'number' ? formatNumber(entry.value) : entry.value}`}
              </p>
            );
          })}
      </div>
    );
  };

  // Custom Legend Content - 根據設備類型動態顯示圖例項
  const renderCustomLegend = () => {
    let legendItems: Array<{key: string; label: string; color: string; isActive: boolean; onClick: () => void}> = [];
    
    if (deviceType === '電力設備') {
      // 電力設備：用電量 > peak > avg > min
      legendItems = [
        {
          key: 'energy',
          label: '用電量 (kWh)',
          color: '#3b82f6',
          isActive: showEnergy,
          onClick: onToggleEnergy
        },
        {
          key: 'peakPower',
          label: '峰值功率 (W)',
          color: '#f97316',
          isActive: showPeakPower,
          onClick: onTogglePeakPower
        },
        {
          key: 'avgPower',
          label: '平均功率 (W)',
          color: '#60a5fa',
          isActive: showAvgPower,
          onClick: onToggleAvgPower
        },
        {
          key: 'minPower',
          label: '最低功率 (W)',
          color: '#9ca3af',
          isActive: showMinPower,
          onClick: onToggleMinPower
        }
      ];
    } else if (deviceType === '溫溼度') {
      // 溫溼度：溫度 > 濕度（始終顯示，不可隱藏）
      legendItems = [
        {
          key: 'temperature',
          label: '溫度 (°C)',
          color: '#f97316',
          isActive: true, // 始終顯示為激活狀態
          onClick: () => {} // 無操作，不可點擊隱藏
        },
        {
          key: 'humidity',
          label: '濕度 (%)',
          color: '#3b82f6',
          isActive: true, // 始終顯示為激活狀態
          onClick: () => {} // 無操作，不可點擊隱藏
        }
      ];
    } else if (deviceType === '空氣品質') {
      // 空氣品質：CO2 > TVOC > PM2.5 > PM10
      legendItems = [
        {
          key: 'co2',
          label: 'CO₂ (ppm)',
          color: '#ef4444',
          isActive: showEnergy,
          onClick: onToggleEnergy
        },
        {
          key: 'tvoc',
          label: 'TVOC (ppb)',
          color: '#f97316',
          isActive: showPeakPower,
          onClick: onTogglePeakPower
        },
        {
          key: 'pm25',
          label: 'PM2.5 (μg/m³)',
          color: '#60a5fa',
          isActive: showAvgPower,
          onClick: onToggleAvgPower
        },
        {
          key: 'pm10',
          label: 'PM10 (μg/m³)',
          color: '#9ca3af',
          isActive: showMinPower,
          onClick: onToggleMinPower
        }
      ];
    }

    return (
      <div style={{ paddingTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap' }}>
          {legendItems.map((item) => (
            <div
              key={item.key}
              onClick={item.onClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                transition: 'opacity 0.2s'
              }}
            >
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: item.isActive ? item.color : 'transparent',
                  border: `2px solid ${item.color}`,
                  display: 'inline-block',
                  flexShrink: 0
                }}
              />
              <span style={{ 
                color: item.isActive ? '#e5e7eb' : '#6b7280', 
                fontSize: '12px',
                transition: 'color 0.2s'
              }}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
        {/* 分隔線 */}
        <div style={{ 
          marginTop: '16px', 
          marginBottom: '12px',
          borderTop: '1px solid #374151',
          width: '100%'
        }} />
        {/* Powered by Ultron Turing */}
        <div style={{ 
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '16px',
          fontWeight: 'bold',
          marginTop: '8px',
          marginBottom: '20px',
          paddingBottom: '20px'
        }}>
          Powered by <span style={{ color: '#f97316' }}>Ultron Turing</span>
        </div>
      </div>
    );
  };
  
  
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart 
        data={data}
        margin={{ top: 5, right: 30, left: 20, bottom: 40 }}
      >
        <defs>
          <linearGradient id="energyGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
        <XAxis
          dataKey="displayTime"
          stroke="#6b7280"
          tick={(props: any) => {
            const { x, y, payload } = props;
            return (
              <g transform={`translate(${x},${y})`}>
                <text
                  x={0}
                  y={0}
                  dy={16}
                  dx={-5}
                  textAnchor="end"
                  fill="#9ca3af"
                  fontSize={11}
                  transform="rotate(-45)"
                >
                  {payload.value}
                </text>
              </g>
            );
          }}
          interval={getXAxisInterval()}
          height={60}  // 為傾斜標籤預留更多空間
        />
        {deviceType === '電力設備' && (
          <>
            {/* 動態顯示 W 或 kWh Y 軸標籤 */}
            {(showPeakPower || showAvgPower || showMinPower) && (
              <YAxis
                yAxisId="left"
                label={{ 
                  value: 'W', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fill: '#ffffff', fontWeight: 'bold', fontSize: '14px' }
                }}
                stroke="#6b7280"
                tick={{ fill: '#9ca3af', fontSize: 11 }}
              />
            )}
            {showEnergy && (
              <YAxis
                yAxisId="right"
                orientation="right"
                label={{ 
                  value: 'kWh', 
                  angle: 90, 
                  position: 'insideRight',
                  style: { fill: '#ffffff', fontWeight: 'bold', fontSize: '14px' }
                }}
                stroke="#6b7280"
                tick={{ fill: '#9ca3af', fontSize: 11 }}
              />
            )}
          </>
        )}
        {deviceType === '溫溼度' && (
          <>
            <YAxis
              yAxisId="left"
              label={{ 
                value: '°C', 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: '#ffffff', fontWeight: 'bold', fontSize: '14px' }
              }}
              stroke="#6b7280"
              tick={{ fill: '#9ca3af', fontSize: 11 }}
              domain={tempDomain || ['dataMin - 2', 'dataMax + 2']}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              label={{ 
                value: 'Humidity %', 
                angle: 90, 
                position: 'insideRight',
                style: { fill: '#ffffff', fontWeight: 'bold', fontSize: '14px' }
              }}
              stroke="#6b7280"
              tick={{ fill: '#9ca3af', fontSize: 11 }}
              domain={[0, 100]}
            />
          </>
        )}
        {deviceType === '空氣品質' && (
          <YAxis
            yAxisId="left"
            label={{ value: 'ppm/ppb/μg/m³', angle: -90, position: 'insideLeft' }}
            stroke="#6b7280"
            tick={{ fill: '#9ca3af', fontSize: 11 }}
          />
        )}
        <Tooltip 
          content={renderCustomTooltip}
        />
        <Legend 
          content={renderCustomLegend}
          wrapperStyle={{ paddingTop: '10px' }}
        />
        {/* 根據設備類型動態渲染圖表線條 */}
        {deviceType === '電力設備' && (
          <>
            {/* 用電量漸層陰影 */}
            {showEnergy && (
              <Area
                yAxisId="right"
                type="monotone"
                dataKey="energy"
                fill="url(#energyGradient)"
                stroke="#3b82f6"
                strokeWidth={0}
                baseLine={0}
                connectNulls={false}
                isAnimationActive={false}
                name=""
                legendType="none"
              />
            )}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="energy"
              name="用電量 (kWh)"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              hide={!showEnergy}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="maxPower"
              name="峰值功率 (W)"
              stroke="#f97316"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              hide={!showPeakPower}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="avgPower"
              name="平均功率 (W)"
              stroke="#60a5fa"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              hide={!showAvgPower}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="minPower"
              name="最低功率 (W)"
              stroke="#9ca3af"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              hide={!showMinPower}
            />
          </>
        )}
        {deviceType === '溫溼度' && (
          <>
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="temperature"
              name="溫度 (°C)"
              stroke="#f97316"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              hide={false} // 始終顯示
              connectNulls={false}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="humidity"
              name="濕度 (%)"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              hide={false} // 始終顯示
              connectNulls={false}
            />
          </>
        )}
        {deviceType === '空氣品質' && (
          <>
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="co2"
              name="CO₂ (ppm)"
              stroke="#ef4444"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              hide={!showEnergy}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="tvoc"
              name="TVOC (ppb)"
              stroke="#f97316"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              hide={!showPeakPower}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="pm25"
              name="PM2.5 (μg/m³)"
              stroke="#60a5fa"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              hide={!showAvgPower}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="pm10"
              name="PM10 (μg/m³)"
              stroke="#9ca3af"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              hide={!showMinPower}
            />
          </>
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

const ReportChart = React.memo(ReportChartComponent);

const GenerateReportPage: React.FC = () => {

  // State
  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(true);
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType>('電力設備');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('week');
  const [showGranularityDropdown, setShowGranularityDropdown] = useState(false);
  const [selectedGranularity, setSelectedGranularity] = useState<'auto' | 'minute' | 'hour' | 'day'>('auto');
  const [activeTab, setActiveTab] = useState<ViewTab>('chart');
  const [isGenerating, setIsGenerating] = useState(false);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Data series visibility
  const [showEnergy, setShowEnergy] = useState(true);
  const [showPeakPower, setShowPeakPower] = useState(true);
  const [showAvgPower, setShowAvgPower] = useState(true);
  const [showMinPower, setShowMinPower] = useState(true);
  
  // Table sort: field and order
  type SortField = 'time' | 'maxPower' | 'avgPower' | 'minPower' | 'energy' | 'temperature' | 'humidity';
  const [tableSort, setTableSort] = useState<{ field: SortField; order: 'asc' | 'desc' }>({
    field: 'time',
    order: 'desc'  // 默認降序：最新的在前
  });

  const searchInputRef = useRef<HTMLInputElement>(null);
  const deviceDropdownRef = useRef<HTMLDivElement>(null);
  const periodDropdownRef = useRef<HTMLDivElement>(null);
  const granularityDropdownRef = useRef<HTMLDivElement>(null);

  // 粒度可用性检查函数
  // MCP 粒度限制：
  // - minute: 最大支持 24 小时
  // - hour: 最大支持 31 天 (744小时)
  // - day: 最大支持 730 天
  const getAvailableGranularities = (period: Period): Array<'auto' | 'minute' | 'hour' | 'day'> => {
    switch (period) {
      case 'week':
        // 7 天 = 168 小时，超过 24 小时，不能使用 minute
        return ['auto', 'hour', 'day'];
      case 'month':
        // 30 天 = 720 小时，超过 24 小时，不能使用 minute；但小于 744 小时，可以使用 hour
        return ['auto', 'hour', 'day'];
      case '6months':
      case 'year':
        // 180 天和 365 天都超过 31 天，只能使用 day
        return ['auto', 'day'];
      default:
        return ['auto', 'hour', 'day'];
    }
  };

  // Fetch devices function - only shows imported devices
  const fetchDevices = useCallback(async () => {
    setIsLoadingDevices(true);
    try {
      // Step 1: Get imported devices from Firestore
      const importedDevices = await getImportedDevices('intelliSpace');
      logger.log(`📋 [GenerateReport] Loaded ${importedDevices.length} imported devices from Firestore`);
      
      if (importedDevices.length === 0) {
        setDevices([]);
        setIsLoadingDevices(false);
        return;
      }
      
      // Step 2: Extract device SNs
      const deviceSNs = importedDevices.map((d: any) => 
        d.deviceSN || d.sn || d.deviceId
      ).filter(Boolean);
      
      if (deviceSNs.length === 0) {
        logger.warn('⚠️ [GenerateReport] No valid device SNs found in imported devices');
        setDevices([]);
        setIsLoadingDevices(false);
        return;
      }
      
      // Step 3: Get unique base SNs and fetch full device info
      const baseSNs = Array.from(new Set(deviceSNs.map((sn: string) => sn.split('#')[0])));
      logger.log(`📋 [GenerateReport] Querying ${baseSNs.length} devices by SN`);
      
      const devicesResult = await ultronTuringApi.queryDevices(undefined, undefined, {
        sns: baseSNs
      });
      
      // Step 4: Merge with Firestore data for nicknames
      const mergedDevices = (devicesResult || []).map((apiDevice: any) => {
        const deviceSN = apiDevice.sn || apiDevice.deviceSN;
        const baseSN = deviceSN ? deviceSN.split('#')[0] : '';
        
        const firestoreDevice = importedDevices.find((d: any) => {
          const dSN = d.deviceSN || d.sn || d.deviceId;
          const dBaseSN = dSN ? dSN.split('#')[0] : '';
          return dSN === deviceSN || dBaseSN === baseSN;
        });
        
        return {
          ...apiDevice,
          nickname: firestoreDevice?.nickname || apiDevice.nickname,
          displayName: firestoreDevice?.displayName || apiDevice.displayName
        };
      });
      
      setDevices(mergedDevices as Device[]);
      logger.log(`✅ [GenerateReport] Loaded ${mergedDevices.length} devices`);
    } catch (err) {
      logger.error('Failed to fetch devices:', err);
      setError('無法載入設備列表');
    } finally {
      setIsLoadingDevices(false);
    }
  }, []);

  // Fetch devices on mount
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Filter devices by type
  const filteredDevices = useMemo(() => {
    let result = devices;

    // Filter by device type - ONLY use capability, attributes, sensorProfile, traits
    if (selectedDeviceType === '電力設備') {
      result = result.filter(d => {
        const deviceType = d.deviceType || d.type || '';
        const traits = d.traits || [];
        const attributes = d.attributes || {};
        const sensorProfile = attributes.sensorProfile || {};
        const capabilities = d.capabilities || [];
        
        // Exclude devices with temperature/humidity (th sensors)
        if (sensorProfile.temp !== undefined || sensorProfile.temperature !== undefined ||
            sensorProfile.rh !== undefined || sensorProfile.humidity !== undefined ||
            d.temperature !== undefined || d.temp !== undefined ||
            d.humidity !== undefined || d.rh !== undefined ||
            attributes.temperature !== undefined || attributes.temp !== undefined ||
            attributes.humidity !== undefined || attributes.rh !== undefined) {
          return false;
        }
        
        // Check by device type
        if (deviceType === 'power_meter' || deviceType === 'POWER_METER') {
          return true;
        }
        
        // Check by traits
        if (traits.includes('EnergyStorage') || traits.includes('PowerMonitoring')) {
          return true;
        }
        
        // Check by capabilities
        if (Array.isArray(capabilities)) {
          if (capabilities.some((cap: any) => 
            cap === 'power_meter' || cap === 'POWER_METER' ||
            (typeof cap === 'object' && cap !== null && (cap.type === 'power_meter' || cap.type === 'POWER_METER'))
          )) {
            return true;
          }
        }
        
        // Check by sensor profile (power metrics) - check all possible power-related fields
        const hasPowerMetrics = 
          sensorProfile.watt !== undefined || sensorProfile.watt_hour !== undefined ||
          sensorProfile.voltage !== undefined || sensorProfile.current !== undefined ||
          sensorProfile.energy !== undefined || sensorProfile.power !== undefined ||
          sensorProfile.wattHour !== undefined || sensorProfile.kwh !== undefined ||
          sensorProfile.kWh !== undefined || sensorProfile.wh !== undefined ||
          d.watt !== undefined || d.wattHour !== undefined ||
          d.voltage !== undefined || d.current !== undefined ||
          d.energy !== undefined || d.power !== undefined ||
          d.kwh !== undefined || d.kWh !== undefined || d.wh !== undefined;
        
        if (hasPowerMetrics) {
          // Include if it has power metrics (even if it has OnOff, it might be a switch/outlet with meter)
          return true;
        }
        
        // Check by attributes directly - check all possible power-related fields
        const hasPowerAttributes = 
          attributes.watt !== undefined || attributes.watt_hour !== undefined ||
          attributes.voltage !== undefined || attributes.current !== undefined ||
          attributes.energy !== undefined || attributes.power !== undefined ||
          attributes.wattHour !== undefined || attributes.kwh !== undefined ||
          attributes.kWh !== undefined || attributes.wh !== undefined;
        
        if (hasPowerAttributes) {
          return true;
        }
        
        // Also check if device has any power-related capability or trait
        if (traits.includes('PowerMonitoring') || traits.includes('EnergyMonitoring') ||
            traits.includes('VoltageMonitoring') || traits.includes('CurrentMonitoring') ||
            traits.includes('EnergyStorage')) {
          return true;
        }
        
        return false;
      });
    } else if (selectedDeviceType === '溫溼度') {
      result = result.filter(d => {
        const attributes = d.attributes || {};
        const sensorProfile = attributes.sensorProfile || {};
        
        // Only check temp/temperature and rh/humidity
        if (sensorProfile.temp !== undefined || sensorProfile.temperature !== undefined ||
            sensorProfile.rh !== undefined || sensorProfile.humidity !== undefined ||
            d.temperature !== undefined || d.temp !== undefined ||
            d.humidity !== undefined || d.rh !== undefined ||
            attributes.temperature !== undefined || attributes.temp !== undefined ||
            attributes.humidity !== undefined || attributes.rh !== undefined) {
          return true;
        }
        
        return false;
      });
    } else if (selectedDeviceType === '空氣品質') {
      result = result.filter(d => {
        const deviceType = d.deviceType || d.type || '';
        const traits = d.traits || [];
        const attributes = d.attributes || {};
        const sensorProfile = attributes.sensorProfile || {};
        const capabilities = d.capabilities || [];
        
        // Check by device type
        if (deviceType === 'air_quality' || deviceType === 'AIR_QUALITY') {
          return true;
        }
        
        // Check by traits
        if (traits.includes('AirQuality') || traits.includes('AirQualitySensor')) {
          return true;
        }
        
        // Check by sensor profile (air quality metrics)
        if (sensorProfile.co2 !== undefined || sensorProfile.tvoc !== undefined ||
            sensorProfile.pm25 !== undefined || sensorProfile.pm10 !== undefined ||
            sensorProfile.airQuality !== undefined ||
            d.co2 !== undefined || d.tvoc !== undefined ||
            d.pm25 !== undefined || d.pm10 !== undefined ||
            d.airQuality !== undefined) {
          return true;
        }
        
        // Check by capabilities
        if (Array.isArray(capabilities)) {
          if (capabilities.some((cap: any) => 
            cap === 'air_quality' || cap === 'AIR_QUALITY' ||
            (typeof cap === 'object' && cap !== null && (
              cap.type === 'air_quality' || cap.type === 'AIR_QUALITY'
            ))
          )) {
            return true;
          }
        }
        
        // Check by attributes directly
        if (attributes.co2 !== undefined || attributes.tvoc !== undefined ||
            attributes.pm25 !== undefined || attributes.pm10 !== undefined ||
            attributes.airQuality !== undefined) {
          return true;
        }
        
        return false;
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d => {
        const sn = (d.sn || '').toLowerCase();
        const name = (d.name || d.displayName || d.nickname || '').toLowerCase();
        return sn.includes(query) || name.includes(query);
      });
    }

    return result;
  }, [devices, selectedDeviceType, searchQuery]);

  // Track if report has been generated (to auto-reload on period change)
  const hasGeneratedReport = useRef(false);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (deviceDropdownRef.current && !deviceDropdownRef.current.contains(event.target as Node)) {
        setShowDeviceDropdown(false);
      }
      if (periodDropdownRef.current && !periodDropdownRef.current.contains(event.target as Node)) {
        setShowPeriodDropdown(false);
      }
      if (granularityDropdownRef.current && !granularityDropdownRef.current.contains(event.target as Node)) {
        setShowGranularityDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle device selection
  const handleSelectDevice = (device: Device) => {
    setSelectedDevice(device);
    setSearchQuery(device.sn || '');
    setShowDeviceDropdown(false);
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedDevice(null);
    setSearchQuery('');
    setChartData([]);
    setError(null);
  };

  // Generate report
  const handleGenerateReport = async () => {
    if (!selectedDevice) {
      setError('請選擇設備');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const reportType = selectedDeviceType === '電力設備' ? 'energy' : 'sensor';
      
      // ✅ 統一數據提取函數（與 IntelliSpaceDashboard 保持一致）
      const extractChartData = (result: any): any[] => {
        let chartData: any[] = [];
        const reportData = result?.data || {};
        
        logger.log('🔍 extractChartData - reportData structure:', {
          hasData: !!reportData,
          reportDataKeys: Object.keys(reportData),
          hasRootChartData: Array.isArray(reportData.chartData),
          rootChartDataLength: Array.isArray(reportData.chartData) ? reportData.chartData.length : 0
        });
        
        // 檢查根級別的 chartData
        if (reportData.chartData && Array.isArray(reportData.chartData)) {
          chartData = reportData.chartData;
          logger.log('✅ Found chartData at root level:', chartData.length);
        } else {
          // 檢查是否是以設備 SN 為鍵的格式 { "UTU500-xxx#1": { chartData: [...] } }
          const deviceKeys = Object.keys(reportData).filter(key => {
            const value = reportData[key];
            return typeof value === 'object' && value !== null && !Array.isArray(value) &&
                   key !== 'devices' && key !== 'totalConsumption' && key !== 'averagePower' && 
                   key !== 'peakPower' && key !== 'deviceType' && key !== 'availableMetrics' &&
                   key !== 'returnedMetrics' && key !== 'dataPoints';
          });
          
          logger.log('🔍 Found device keys:', deviceKeys);
          
          // 合併所有設備的 chartData
          deviceKeys.forEach(deviceKey => {
            const deviceData = reportData[deviceKey] as any;
            logger.log(`🔍 Checking device ${deviceKey}:`, {
              hasChartData: !!deviceData?.chartData,
              chartDataIsArray: Array.isArray(deviceData?.chartData),
              chartDataLength: Array.isArray(deviceData?.chartData) ? deviceData.chartData.length : 0,
              deviceDataKeys: deviceData ? Object.keys(deviceData) : []
            });
            
            if (deviceData?.chartData && Array.isArray(deviceData.chartData)) {
              chartData = chartData.concat(deviceData.chartData);
              logger.log(`✅ Added ${deviceData.chartData.length} data points from ${deviceKey}`);
            }
          });
        }
        
        logger.log('📊 extractChartData result:', {
          totalChartDataLength: chartData.length,
          firstPoint: chartData[0],
          lastPoint: chartData[chartData.length - 1]
        });
        
        return chartData;
      };
      
      // ✅ 新 MCP API 格式：使用 deviceSNs, duration, granularity, format
      let duration: string;
      let granularity: 'minute' | 'hour' | 'day' | 'auto' = 'auto';

      // 设置 duration
      if (selectedPeriod === 'week') {
        duration = '7d';
      } else if (selectedPeriod === 'month') {
        duration = '30d';
      } else if (selectedPeriod === '6months') {
        duration = '180d';
      } else if (selectedPeriod === 'year') {
        duration = '365d';
      } else {
        // 默認：最近一週
        duration = '7d';
      }

      // 设置 granularity：如果用户选择了 AUTO，使用自动逻辑；否则使用用户选择的粒度
      if (selectedGranularity === 'auto') {
        // 自动选择粒度
        if (selectedPeriod === 'week') {
          granularity = 'hour'; // 一週數據使用小時粒度
        } else if (selectedPeriod === 'month') {
          granularity = 'day'; // 一個月數據使用日粒度
        } else if (selectedPeriod === '6months') {
          granularity = 'day'; // 半年數據使用日粒度
        } else if (selectedPeriod === 'year') {
          granularity = 'day'; // 一年數據使用日粒度
        } else {
          granularity = 'hour';
        }
      } else {
        // 使用用户选择的粒度
        granularity = selectedGranularity;
      }

      // ✅ 使用完整的 deviceId（包含 endpoint，如 'UTU500-4J2222M#1'）
      const deviceId = selectedDevice.deviceId || selectedDevice.sn;
      
      logger.log('📊 GenerateReport request params:', {
        deviceSNs: [deviceId],
        duration,
        granularity,
        format: 'summary'
      });
      
      // ✅ 使用 duration + granularity + format 格式
      const reportResult = await ultronTuringApi.generateReport(
        [deviceId], // 使用完整的 deviceId（包含 endpoint）
        {
          duration,
          granularity,
          format: 'summary'
        }
      );

      if (reportResult?.success === false) {
        const errorMsg = typeof reportResult.error === 'string' ? reportResult.error : '生成報告失敗';
        // 優化錯誤信息顯示
        if (errorMsg.includes('Failed to fetch') || errorMsg.includes('fetch')) {
          setError('網絡連接失敗，請檢查網絡連接或稍後再試');
        } else {
          setError(errorMsg);
        }
        setChartData([]);
        return;
      }

      logger.log('📊 GenerateReport result:', reportResult);
      
      
      // ✅ 使用統一的 extractChartData 函數提取數據
      let chartData: any[] | null = extractChartData(reportResult);
      const reportData = reportResult?.data || {};
      
      logger.log('📊 Extracted chartData:', {
        chartDataLength: chartData?.length || 0,
        reportDataKeys: Object.keys(reportData),
        reportDataSample: reportData && typeof reportData === 'object' ? 
          Object.keys(reportData).slice(0, 3).map(key => {
            const value = reportData[key] as any;
            return {
              key,
              type: typeof value,
              isArray: Array.isArray(value),
              hasChartData: value?.chartData !== undefined
            };
          }) : []
      });
      
      // ✅ 向後兼容：處理舊版感測器設備格式（temp/rh 數組）
      // 如果 extractChartData 沒有找到 chartData，嘗試從舊格式提取
      if (!chartData || chartData.length === 0) {
        if (reportData && typeof reportData === 'object') {
          const deviceKeys = Object.keys(reportData);
          for (const deviceKey of deviceKeys) {
            const deviceData = reportData[deviceKey] as any;
            
            // 檢查是否有 temp/rh/power 數組（舊版感測器設備格式）
            if (deviceData && (Array.isArray(deviceData.temp) || Array.isArray(deviceData.rh) || Array.isArray(deviceData.temperature))) {
              // 合併 temp, rh, power 數組為統一的 chartData 格式
              const tempArray = deviceData.temp || deviceData.temperature || [];
              const rhArray = deviceData.rh || deviceData.humidity || [];
              const powerArray = deviceData.power || [];
              
              // 收集所有唯一的時間戳
              const timestampSet = new Set<number>();
              tempArray.forEach((item: any) => {
                if (item.timestamp) timestampSet.add(item.timestamp);
              });
              rhArray.forEach((item: any) => {
                if (item.timestamp) timestampSet.add(item.timestamp);
              });
              powerArray.forEach((item: any) => {
                if (item.timestamp) timestampSet.add(item.timestamp);
              });
              
              // 按時間戳排序
              const timestamps = Array.from(timestampSet).sort((a, b) => a - b);
              
              // 創建時間戳到數據的映射
              const tempMap = new Map<number, any>();
              tempArray.forEach((item: any) => {
                if (item.timestamp) tempMap.set(item.timestamp, item);
              });
              const rhMap = new Map<number, any>();
              rhArray.forEach((item: any) => {
                if (item.timestamp) rhMap.set(item.timestamp, item);
              });
              const powerMap = new Map<number, any>();
              powerArray.forEach((item: any) => {
                if (item.timestamp) powerMap.set(item.timestamp, item);
              });
              
              // 合併數據
              chartData = timestamps.map(timestamp => {
                const tempData = tempMap.get(timestamp);
                const rhData = rhMap.get(timestamp);
                const powerData = powerMap.get(timestamp);
                
                // 轉換時間戳為 ISO 字符串
                const timeStr = new Date(timestamp * 1000).toISOString();
                
                return {
                  time: timeStr,
                  timestamp: timeStr,
                  temperature: tempData?.value,
                  temp: tempData?.value,
                  tempC: tempData?.value,
                  humidity: rhData?.value,
                  rh: rhData?.value,
                  humidityPercent: rhData?.value,
                  power: powerData?.value
                };
              });
              
              break;
            }
          }
        }
      }
      
      logger.log('📊 Report data structure:', {
        hasData: !!reportData,
        hasChartData: !!chartData,
        chartDataLength: chartData?.length || 0,
        chartDataSample: chartData?.[0],
        chartDataLast: chartData?.[chartData?.length - 1],
        dataKeys: reportData ? Object.keys(reportData) : [],
        selectedPeriod: selectedPeriod,
        duration: duration,
        granularity: granularity,
        deviceType: selectedDeviceType,
        // 检查温湿度数据格式
        firstPointHasTemp: chartData?.[0]?.temp !== undefined,
        firstPointHasRh: chartData?.[0]?.rh !== undefined,
        firstPointHasTemperature: chartData?.[0]?.temperature !== undefined,
        firstPointHasHumidity: chartData?.[0]?.humidity !== undefined
      });
      
      
      // Process chartData - 根據設備類型動態處理數據
      if (chartData && chartData.length > 0) {
        const processedData: ChartDataPoint[] = chartData.map((point: any, index: number) => {
          // 格式化时间用于图表显示
          // ✅ 新 MCP API 返回的时间已经根据传入的 utcOffset 转换好了
          // 所以这里直接使用返回的时间数据，不需要再进行时区转换
          // 返回的时间可能是 ISO 字符串或 Unix 时间戳
          let timeStr = point.time || point.timestamp || '';
          let date: Date | null = null;
          
          // 如果 timeStr 是數字（Unix 時間戳），轉換為毫秒
          if (typeof timeStr === 'number') {
            // 判斷是秒還是毫秒（通常 > 1e12 是毫秒，否則是秒）
            date = new Date(timeStr > 1e12 ? timeStr : timeStr * 1000);
          } else if (typeof timeStr === 'string') {
            date = new Date(timeStr);
          }
          
          let displayTime = timeStr.toString();
          try {
            if (date && !isNaN(date.getTime())) {
              // 月份名稱陣列（前三個字母大寫）
              const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
              const monthIndex = date.getMonth();
              const monthAbbr = monthNames[monthIndex];
              const day = date.getDate().toString().padStart(2, '0');  // 補足兩位
              
              // 如果是日粒度，只顯示日期
              if (granularity === 'day') {
                displayTime = `${monthAbbr} / ${day}`;
              } else {
                // 小時或分鐘粒度，顯示日期和時間
                const hour = date.getHours().toString().padStart(2, '0');
                const minute = date.getMinutes().toString().padStart(2, '0');
                displayTime = `${monthAbbr} / ${day} ${hour}:${minute}`;
              }
              
              // 確保 timeStr 是 ISO 字符串格式（用於後續處理和排序）
              if (typeof timeStr !== 'string' || !timeStr || timeStr === '') {
                timeStr = date.toISOString();
              }
            } else {
              // 日期無效，記錄警告並使用原始值
              logger.warn('⚠️ Invalid date:', { timeStr, point });
              displayTime = typeof timeStr === 'string' ? timeStr.substring(0, 16).replace('T', ' ') : String(timeStr);
              // 如果日期無效，嘗試生成一個有效的 ISO 字符串
              if (typeof timeStr !== 'string' || !timeStr) {
                timeStr = new Date().toISOString(); // 使用當前時間作為備用
              }
            }
          } catch (e) {
            logger.warn('⚠️ Error parsing time:', { timeStr, point, error: e });
            displayTime = typeof timeStr === 'string' ? timeStr : String(timeStr);
            // 如果解析失敗，使用當前時間作為備用
            if (typeof timeStr !== 'string' || !timeStr) {
              timeStr = new Date().toISOString();
            }
          }
          
          // 最終確保 time 字段是有效的 ISO 字符串（用於排序）
          const finalTimeStr = (date && !isNaN(date.getTime())) 
            ? date.toISOString() 
            : (typeof timeStr === 'string' && timeStr ? timeStr : new Date().toISOString());
          
          // 根據設備類型處理不同的數據
          if (selectedDeviceType === '電力設備') {
            // ✅ 新 MCP API 優先使用 energy 或 periodEnergy（差值）
            // 備用方案：如果沒有差值字段，使用 avgPower/power 計算
            let energy: number | undefined = undefined;
            
            // 優先使用 API 返回的差值字段
            if (point.energy !== undefined) {
              energy = typeof point.energy === 'number' ? point.energy : parseFloat(String(point.energy)) || undefined;
            } else if (point.periodEnergy !== undefined) {
              energy = typeof point.periodEnergy === 'number' ? point.periodEnergy : parseFloat(String(point.periodEnergy)) || undefined;
            } else if (point.avgPower !== undefined || point.power !== undefined) {
              // 如果沒有直接的能量差值，使用平均功率計算
              const avgPowerW = typeof point.avgPower === 'number' ? point.avgPower : 
                               (typeof point.power === 'number' ? point.power : 0);
              // 根據粒度估算：假設是小時粒度，energy = power (W) * 1h / 1000 = kWh
              // 如果是日粒度，energy = power (W) * 24h / 1000 = kWh
              // 這裡假設是小時粒度，如果是其他粒度需要根據實際情況調整
              energy = (avgPowerW / 1000); // 小時粒度：1 小時的用電量
            }
            
            const sanitizePower = (val: number | undefined): number | undefined => {
              if (typeof val !== 'number' || isNaN(val) || !isFinite(val)) return undefined;
              if (val < 0 || val >= 1000000) return undefined;
              return val;
            };

            const sanitizeEnergy = (val: number | undefined): number | undefined => {
              if (typeof val !== 'number' || isNaN(val) || !isFinite(val)) return undefined;
              if (val < 0 || val >= 10000) return undefined;
              return val;
            };

            return {
              time: finalTimeStr,  // 使用標準化的 ISO 字符串，確保排序正確
              displayTime: displayTime,
              avgPower: sanitizePower(typeof point.avgPower === 'number' ? point.avgPower : 
                       (typeof point.watt === 'number' ? point.watt : undefined)),
              energy: sanitizeEnergy(energy),
              minPower: sanitizePower(typeof point.minPower === 'number' ? point.minPower : undefined),
              maxPower: sanitizePower(typeof point.peakPower === 'number' ? point.peakPower : 
                       (typeof point.maxPower === 'number' ? point.maxPower : undefined))
            };
          } else if (selectedDeviceType === '溫溼度') {
            // 溫溼度：處理 temperature, humidity
            const sanitizeTemp = (val: number | undefined): number | undefined => {
              if (typeof val !== 'number' || isNaN(val) || !isFinite(val)) return undefined;
              if (val < -50 || val > 100) return undefined; // 合理溫度範圍
              return val;
            };

            const sanitizeHumidity = (val: number | undefined): number | undefined => {
              if (typeof val !== 'number' || isNaN(val) || !isFinite(val)) return undefined;
              if (val < 0 || val > 100) return undefined; // 濕度範圍 0-100%
              return val;
            };

            // 提取温度值（支持多种字段名）
            const tempValue = typeof point.temperature === 'number' ? point.temperature : 
                             (typeof point.temp === 'number' ? point.temp : 
                              (typeof point.tempC === 'number' ? point.tempC : undefined));
            
            // 提取湿度值（支持多种字段名）
            const humidityValue = typeof point.humidity === 'number' ? point.humidity : 
                                 (typeof point.rh === 'number' ? point.rh : 
                                  (typeof point.humidityPercent === 'number' ? point.humidityPercent : undefined));

            // 调试日志：记录前几个数据点的原始值
            if (index < 3) {
              logger.log(`🌡️ 温湿度数据处理 [${index}]:`, {
                rawPoint: point,
                tempValue,
                humidityValue,
                sanitizedTemp: sanitizeTemp(tempValue),
                sanitizedHumidity: sanitizeHumidity(humidityValue)
              });
            }


            return {
              time: finalTimeStr,  // 使用標準化的 ISO 字符串，確保排序正確
              displayTime: displayTime,
              temperature: sanitizeTemp(tempValue),
              humidity: sanitizeHumidity(humidityValue)
            };
          } else if (selectedDeviceType === '空氣品質') {
            // 空氣品質：處理 co2, tvoc, pm25, pm10
            const sanitizeAirQuality = (val: number | undefined): number | undefined => {
              if (typeof val !== 'number' || isNaN(val) || !isFinite(val)) return undefined;
              if (val < 0 || val > 100000) return undefined; // 合理範圍
              return val;
            };

            return {
              time: finalTimeStr,  // 使用標準化的 ISO 字符串，確保排序正確
              displayTime: displayTime,
              co2: sanitizeAirQuality(typeof point.co2 === 'number' ? point.co2 : undefined),
              tvoc: sanitizeAirQuality(typeof point.tvoc === 'number' ? point.tvoc : undefined),
              pm25: sanitizeAirQuality(typeof point.pm25 === 'number' ? point.pm25 : undefined),
              pm10: sanitizeAirQuality(typeof point.pm10 === 'number' ? point.pm10 : undefined)
            };
          }
          
          // 默認返回空數據
          return {
            time: finalTimeStr,  // 使用標準化的 ISO 字符串，確保排序正確
            displayTime: displayTime
          };
        });
        
        logger.log('📊 Processed data info:', {
          processedLength: processedData.length,
          firstDataPoint: processedData[0],
          lastDataPoint: processedData[processedData.length - 1],
          dateRange: processedData.length > 0 ? {
            start: processedData[0].time,
            end: processedData[processedData.length - 1].time
          } : null
        });
        
        // ✅ 簡化第一個數據點的處理：新 MCP API 直接返回 energy/periodEnergy 差值
        // 如果第一個數據點沒有 energy 值，嘗試使用第二個數據點的值（假設時間段相同），或設為 0
        if (processedData.length > 0 && processedData[0].energy === undefined) {
          if (processedData.length > 1 && processedData[1].energy !== undefined) {
            // 使用第二個數據點的 energy 值（假設時間段相同）
            processedData[0].energy = processedData[1].energy;
          } else {
            // 如果無法估算，設為 0
            processedData[0].energy = 0;
          }
        }
        
        // 过滤掉无效的数据点
        logger.log('🔍 过滤前数据:', {
          totalPoints: processedData.length,
          samplePoints: processedData.slice(0, 3).map(p => ({
            time: p.displayTime,
            temperature: p.temperature,
            humidity: p.humidity,
            hasTemp: p.temperature !== undefined,
            hasHumidity: p.humidity !== undefined
          })),
          deviceType: selectedDeviceType
        });

        const filteredData = processedData.filter(point => {
          if (!point.time) return false;
          // 对于温湿度设备，至少需要有温度或湿度数据
          if (selectedDeviceType === '溫溼度') {
            const hasData = point.temperature !== undefined || point.humidity !== undefined;
            if (!hasData && processedData.indexOf(point) < 3) {
              logger.warn('⚠️ 过滤掉无效的温湿度数据点:', {
                time: point.displayTime,
                temperature: point.temperature,
                humidity: point.humidity
              });
            }
            return hasData;
          }
          // 对于电力设备，至少需要有功率或用电量数据
          if (selectedDeviceType === '電力設備') {
            return point.avgPower !== undefined || point.maxPower !== undefined || 
                   point.minPower !== undefined || point.energy !== undefined;
          }
          // 对于空气品质设备，至少需要有一个指标
          if (selectedDeviceType === '空氣品質') {
            return point.co2 !== undefined || point.tvoc !== undefined || 
                   point.pm25 !== undefined || point.pm10 !== undefined;
          }
          return true;
        });

        logger.log('🔍 过滤后数据:', {
          totalPoints: filteredData.length,
          filteredOut: processedData.length - filteredData.length,
          samplePoints: filteredData.slice(0, 3).map(p => ({
            time: p.displayTime,
            temperature: p.temperature,
            humidity: p.humidity
          }))
        });

        logger.log('📊 Processed data:', {
          count: filteredData.length,
          sample: filteredData[0],
          hasEnergy: filteredData.some(p => p.energy !== undefined),
          hasTemperature: filteredData.some(p => p.temperature !== undefined),
          hasHumidity: filteredData.some(p => p.humidity !== undefined),
          deviceType: selectedDeviceType,
          // 显示前几个数据点的详细信息
          firstFewPoints: filteredData.slice(0, 3).map(p => ({
            time: p.displayTime,
            temperature: p.temperature,
            humidity: p.humidity,
            energy: p.energy,
            avgPower: p.avgPower
          }))
        });


        if (filteredData.length > 0) {
          // 创建新数组引用，确保 React 能检测到变化
          setChartData([...filteredData]);
          setError(null);
          // 標記已經產生過報表
          hasGeneratedReport.current = true;
          
        } else {
          setError('沒有可用的數據');
          setChartData([]);
          
        }
      } else {
        // 检查是否有错误信息
        const errorMsg = typeof reportResult?.error === 'string' ? reportResult.error :
                        typeof reportResult?.message === 'string' ? reportResult.message :
                        '沒有可用的數據';
        logger.warn('⚠️ No chartData found:', { reportResult, errorMsg });
        setError(errorMsg);
        setChartData([]);
      }
    } catch (err) {
      logger.error('Failed to generate report:', err);
      // 優化錯誤信息顯示
      if (err instanceof Error) {
        if (err.message.includes('Failed to fetch') || err.message.includes('fetch')) {
          setError('網絡連接失敗，請檢查網絡連接或稍後再試');
        } else if (err.message.includes('timeout') || err.message.includes('Timeout')) {
          setError('請求超時，請稍後再試');
        } else {
          setError(`生成報告時發生錯誤: ${err.message}`);
        }
      } else {
        setError('生成報告時發生錯誤');
      }
      setChartData([]);
    } finally {
      setIsGenerating(false);
    }
  };

  // Auto-adjust granularity when period changes
  useEffect(() => {
    const availableGranularities = getAvailableGranularities(selectedPeriod);
    if (!availableGranularities.includes(selectedGranularity)) {
      setSelectedGranularity('auto');
    }
  }, [selectedPeriod, selectedGranularity]);

  // Auto-reload report when period changes (only if already generated)
  const previousPeriodRef = useRef(selectedPeriod);
  useEffect(() => {
    // 只有當 period 真的改變且已經產生過報表時才自動重新載入
    if (previousPeriodRef.current !== selectedPeriod && hasGeneratedReport.current && selectedDevice) {
      previousPeriodRef.current = selectedPeriod;
      handleGenerateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedDevice]);

  // Get device display name
  const getDeviceDisplayName = (device: Device): string => {
    return device.nickname || device.displayName || device.name || device.sn || 'Unknown';
  };

  // Get date range string for subtitle
  const getDateRangeString = (period: Period): string => {
    const now = new Date();
    let startDate: Date;
    let endDate = new Date(now);

    if (period === 'week') {
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 7);
    } else if (period === 'month') {
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
    } else if (period === '6months') {
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 180);
    } else if (period === 'year') {
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 365);
    } else {
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 7);
    }

    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Get period label
  const getPeriodLabel = (period: Period): string => {
    switch (period) {
      case 'week':
        return '最近一週';
      case 'month':
        return '最近一個月';
      case '6months':
        return '最近六個月';
      case 'year':
        return '最近一年';
      default:
        return '最近一週';
    }
  };

  // Sorted table data based on selected field
  const sortedTableData = useMemo(() => {
    if (!chartData.length) return [];
    
    logger.log('🔄 Starting sort:', {
      field: tableSort.field,
      order: tableSort.order,
      dataLength: chartData.length,
      firstTime: chartData[0]?.time,
      firstTimeType: typeof chartData[0]?.time
    });
    
    const sorted = [...chartData].sort((a, b) => {
      let valueA: number;
      let valueB: number;
      
      switch (tableSort.field) {
        case 'time':
          // 處理時間排序：支持 ISO 字符串和 Unix 時間戳
          const timeA = a.time || '';
          const timeB = b.time || '';
          
          // 如果是數字（Unix 時間戳），轉換為毫秒
          if (typeof timeA === 'number') {
            valueA = timeA > 1e12 ? timeA : timeA * 1000;
          } else if (typeof timeA === 'string' && timeA) {
            const dateA = new Date(timeA);
            valueA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
            // 如果解析失敗，記錄警告
            if (isNaN(dateA.getTime())) {
              logger.warn('⚠️ Invalid timeA for sorting:', timeA);
            }
          } else {
            valueA = 0;
          }
          
          if (typeof timeB === 'number') {
            valueB = timeB > 1e12 ? timeB : timeB * 1000;
          } else if (typeof timeB === 'string' && timeB) {
            const dateB = new Date(timeB);
            valueB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();
            // 如果解析失敗，記錄警告
            if (isNaN(dateB.getTime())) {
              logger.warn('⚠️ Invalid timeB for sorting:', timeB);
            }
          } else {
            valueB = 0;
          }
          
          // 確保值有效
          if (isNaN(valueA)) {
            logger.warn('⚠️ valueA is NaN after processing:', { timeA, valueA });
            valueA = 0;
          }
          if (isNaN(valueB)) {
            logger.warn('⚠️ valueB is NaN after processing:', { timeB, valueB });
            valueB = 0;
          }
          
          break;
        case 'maxPower':
          valueA = a.maxPower ?? 0;
          valueB = b.maxPower ?? 0;
          break;
        case 'avgPower':
          valueA = a.avgPower ?? 0;
          valueB = b.avgPower ?? 0;
          break;
        case 'minPower':
          valueA = a.minPower ?? 0;
          valueB = b.minPower ?? 0;
          break;
        case 'energy':
          valueA = a.energy ?? 0;
          valueB = b.energy ?? 0;
          break;
        case 'temperature':
          valueA = a.temperature ?? 0;
          valueB = b.temperature ?? 0;
          break;
        case 'humidity':
          valueA = a.humidity ?? 0;
          valueB = b.humidity ?? 0;
          break;
        default:
          return 0;
      }
      
      // 處理排序方向
      const comparison = valueA - valueB;
      if (tableSort.order === 'asc') {
        // 升序：從小到大
        return comparison;
      } else {
        // 降序：從大到小（反轉比較結果）
        return -comparison;
      }
    });
    
    // 驗證排序結果
    if (tableSort.field === 'time' && sorted.length > 1) {
      const firstTime = new Date(sorted[0].time).getTime();
      const lastTime = new Date(sorted[sorted.length - 1].time).getTime();
      const isAscending = firstTime < lastTime;
      const expectedAscending = tableSort.order === 'asc';
      
      if (isAscending !== expectedAscending) {
        logger.warn('⚠️ Sort order mismatch!', {
          expectedOrder: tableSort.order,
          expectedAscending,
          actualAscending: isAscending,
          firstTime: sorted[0].time,
          lastTime: sorted[sorted.length - 1].time
        });
      }
    }
    
    logger.log('📊 Table sorted:', {
      field: tableSort.field,
      order: tableSort.order,
      totalItems: sorted.length,
      firstItem: sorted[0]?.time,
      lastItem: sorted[sorted.length - 1]?.time,
      firstDisplayTime: sorted[0]?.displayTime,
      lastDisplayTime: sorted[sorted.length - 1]?.displayTime,
      sampleTimes: sorted.slice(0, 5).map(item => ({
        time: item.time,
        displayTime: item.displayTime,
        timeType: typeof item.time
      }))
    });
    
    return sorted;
  }, [chartData, tableSort]);

  // Download functions
  const handleDownloadCSV = () => {
    if (sortedTableData.length === 0) return;

    // CSV 格式：數值不使用千分號，避免逗號造成解析問題
    const formatNumberForCSV = (value: number | undefined | null): string => {
      if (value === undefined || value === null || isNaN(value)) return '-';
      return value.toFixed(2);
    };

    let headers: string[];
    let rows: string[][];

    if (selectedDeviceType === '電力設備') {
      headers = ['時間', '峰值功率(W)', '平均功率(W)', '最低功率(W)', '用電量(kWh)'];
      rows = sortedTableData.map(point => [
        point.displayTime,
        formatNumberForCSV(point.maxPower),
        formatNumberForCSV(point.avgPower),
        formatNumberForCSV(point.minPower),
        formatNumberForCSV(point.energy)
      ]);
    } else if (selectedDeviceType === '溫溼度') {
      headers = ['時間', '溫度(°C)', '濕度(%)'];
      rows = sortedTableData.map(point => [
        point.displayTime,
        formatNumberForCSV(point.temperature),
        formatNumberForCSV(point.humidity)
      ]);
    } else {
      // 空氣品質或其他類型
      headers = ['時間', '峰值功率(W)', '平均功率(W)', '最低功率(W)', '用電量(kWh)'];
      rows = sortedTableData.map(point => [
        point.displayTime,
        formatNumberForCSV(point.maxPower),
        formatNumberForCSV(point.avgPower),
        formatNumberForCSV(point.minPower),
        formatNumberForCSV(point.energy)
      ]);
    }

    // 用引號包圍每個值，避免特殊字符造成問題
    const escapeCSV = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `報告_${selectedDevice?.sn || 'device'}_${selectedPeriod}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleDownloadPNG = async () => {
    if (!chartContainerRef.current) return;
    
    try {
      // 動態導入 html2canvas
      // @ts-ignore - html2canvas may not be installed
      const html2canvas = await import('html2canvas').catch(() => null);
      if (!html2canvas) {
        alert('請先安裝 html2canvas: npm install html2canvas');
        return;
      }
      
      // 截取整個圖表容器（包含圖表、圖例和 footer）
      const containerElement = chartContainerRef.current;
      if (!containerElement) {
        alert('無法找到圖表容器');
        return;
      }

      const canvas = await html2canvas.default(containerElement, {
        backgroundColor: '#111827', // dark background
        scale: 2,
        logging: false
      });

      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `報告_${selectedDevice?.sn || 'chart'}_${new Date().toISOString().slice(0, 10)}.png`;
      link.href = url;
      link.click();
    } catch (error) {
      logger.error('PNG 下載失敗:', error);
      alert('PNG 下載失敗: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleDownloadPDF = async () => {
    if (!chartContainerRef.current) return;
    
    try {
      // 動態導入 html2canvas 和 jspdf
      // @ts-ignore - html2canvas may not be installed
      const html2canvas = await import('html2canvas').catch(() => null);
      // @ts-ignore - jspdf may not be installed
      const jsPDFModule = await import('jspdf').catch(() => null);
      
      if (!html2canvas || !jsPDFModule) {
        alert('請先安裝依賴: npm install html2canvas jspdf');
        return;
      }
      
      // 截取整個圖表容器（包含圖表、圖例和 footer）
      const containerElement = chartContainerRef.current;
      if (!containerElement) {
        alert('無法找到圖表容器');
        return;
      }

      const canvas = await html2canvas.default(containerElement, {
        backgroundColor: '#111827', // dark background
        scale: 2,
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      // jsPDF 的正確使用方式
      const { jsPDF } = jsPDFModule;
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`報告_${selectedDevice?.sn || 'chart'}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      logger.error('PDF 下載失敗:', error);
      alert('PDF 下載失敗: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleDownloadTablePDF = async () => {
    if (!tableContainerRef.current) return;
    
    try {
      // 動態導入 html2canvas 和 jspdf
      // @ts-ignore - html2canvas may not be installed
      const html2canvas = await import('html2canvas').catch(() => null);
      // @ts-ignore - jspdf may not be installed
      const jsPDFModule = await import('jspdf').catch(() => null);
      
      if (!html2canvas || !jsPDFModule) {
        alert('請先安裝依賴: npm install html2canvas jspdf');
        return;
      }
      
      // 截取整個表格容器（包含標題、標籤和表格）
      const containerElement = tableContainerRef.current;
      if (!containerElement) {
        alert('無法找到表格容器');
        return;
      }

      const canvas = await html2canvas.default(containerElement, {
        backgroundColor: '#ffffff', // white background for table
        scale: 2,
        logging: false
      });

      // jsPDF 的正確使用方式 - 改為直式
      const { jsPDF } = jsPDFModule;
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // 計算寬度比例，使圖片寬度剛好填滿 PDF 寬度（留一點邊距）
      const margin = 5; // 左右各留 5mm 邊距
      const contentWidth = pdfWidth - (margin * 2);
      const widthRatio = contentWidth / imgWidth;
      const scaledWidth = contentWidth;
      const scaledHeight = imgHeight * widthRatio;
      
      // 計算需要多少頁
      const totalPages = Math.ceil(scaledHeight / pdfHeight);
      
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }
        
        // 計算當前頁要顯示的圖片區域
        const sourceY = (page * pdfHeight) / widthRatio;
        const sourceHeight = Math.min(pdfHeight / widthRatio, imgHeight - sourceY);
        const displayHeight = sourceHeight * widthRatio;
        
        // 如果還有內容要顯示
        if (sourceY < imgHeight && sourceHeight > 0) {
          // 創建臨時 canvas 來裁剪當前頁的內容
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = imgWidth;
          tempCanvas.height = sourceHeight;
          const tempCtx = tempCanvas.getContext('2d');
          if (tempCtx) {
            tempCtx.drawImage(canvas, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);
            const pageImgData = tempCanvas.toDataURL('image/png');
            pdf.addImage(pageImgData, 'PNG', margin, 0, scaledWidth, displayHeight);
          }
        }
      }
      
      pdf.save(`報告_${selectedDevice?.sn || 'table'}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      logger.error('PDF 下載失敗:', error);
      alert('PDF 下載失敗: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto p-6">
        {/* Control Panel */}
        <div className="mb-6">
          <div className="bg-[#0C2340] rounded-lg shadow-sm border border-slate-700/30 p-4">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Device Type Selector */}
              <div className="relative min-w-[200px]">
                <select
                  value={selectedDeviceType}
                  onChange={(e) => {
                    setSelectedDeviceType(e.target.value as DeviceType);
                    setSelectedDevice(null);
                    setSearchQuery('');
                  }}
                  className="w-full pl-10 pr-10 py-2 border border-slate-700/50 rounded-lg bg-slate-800/50 text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none cursor-pointer [&::-ms-expand]:hidden"
                  style={{ backgroundImage: 'none' }}
                >
                  <option value="電力設備" className="text-white">電力設備</option>
                  <option value="溫溼度" className="text-white">溫溼度</option>
                  <option value="空氣品質" className="text-white">空氣品質</option>
                </select>
                {selectedDeviceType === '電力設備' && (
                  <Zap className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-500 pointer-events-none" />
                )}
                {selectedDeviceType === '溫溼度' && (
                  <Thermometer className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-orange-500 pointer-events-none" />
                )}
                {selectedDeviceType === '空氣品質' && (
                  <Wind className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-cyan-500 pointer-events-none" />
                )}
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>

              {/* Device Search/Selector */}
              <div className="relative flex-1 min-w-[300px]" ref={deviceDropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={selectedDevice ? selectedDevice.sn : searchQuery}
                    onChange={(e) => {
                      if (!selectedDevice) {
                        setSearchQuery(e.target.value);
                        setShowDeviceDropdown(true);
                      }
                    }}
                    onFocus={() => {
                      if (!selectedDevice) {
                        setShowDeviceDropdown(true);
                      }
                    }}
                    placeholder={selectedDevice ? '' : devices.length === 0 ? '你的系統中沒有可用設備' : `搜尋設備 SN 或名稱 (找到 ${filteredDevices.length} 個設備)`}
                    className="w-full pl-10 pr-10 py-2 border border-slate-700/50 rounded-lg bg-slate-800/50 text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    readOnly={!!selectedDevice}
                  />
                  {selectedDevice && (
                    <button
                      onClick={handleClearSelection}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-400 hover:text-slate-200"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Device Dropdown */}
                {showDeviceDropdown && !selectedDevice && (
                  <div className="absolute z-[100] w-full mt-1 bg-[#0C2340] border border-slate-700/30 rounded-lg shadow-xl max-h-60 overflow-hidden flex flex-col">
                    {filteredDevices.length > 0 ? (
                      <>
                        <div className="px-4 py-2 border-b border-slate-700/30 bg-slate-800/30 bg-[#0C2340]/50">
                          <div className="text-xs font-medium text-slate-400">
                            找到 {filteredDevices.length} 個設備
                          </div>
                        </div>
                        <div className="overflow-y-auto max-h-52">
                          {filteredDevices.slice(0, 20).map((device) => (
                            <button
                              key={device.sn}
                              onClick={() => handleSelectDevice(device)}
                              className="w-full text-left px-4 py-2 hover:bg-slate-700/50 transition-colors"
                            >
                              <div className="font-medium text-white">
                                {device.sn}
                              </div>
                              <div className="text-sm text-slate-400">
                                {getDeviceDisplayName(device)}
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    ) : devices.length === 0 ? (
                      <div className="px-4 py-4 text-center">
                        <AlertCircle className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                        <div className="text-sm text-slate-400">你的系統中沒有可用設備</div>
                        <div className="text-xs text-slate-500 mt-1">請先在設備管理中匯入設備</div>
                      </div>
                    ) : (
                      <div className="px-4 py-3 text-sm text-slate-400 text-center">
                        找不到符合條件的設備
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Refresh Devices Button */}
              <button
                onClick={fetchDevices}
                disabled={isLoadingDevices}
                className="p-2 border border-slate-700/50 rounded-full bg-slate-800/50 text-slate-400 hover:bg-slate-700/30 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="重新載入設備列表"
              >
                <RefreshCw className={`w-5 h-5 ${isLoadingDevices ? 'animate-spin' : ''}`} />
              </button>

              {/* Date Range Button */}
              <div className="relative" ref={periodDropdownRef}>
                <button
                  onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                  className="px-4 py-2 border border-slate-700/50 rounded-lg bg-slate-800/50 text-white hover:bg-slate-700/30 hover:bg-slate-600 transition-colors flex items-center gap-2"
                >
                  <span>
                    {selectedPeriod === 'week' ? '最近一週' : selectedPeriod === 'month' ? '最近一個月' : selectedPeriod === '6months' ? '最近六個月' : '最近一年'}
                  </span>
                  <Calendar className="w-4 h-4 text-slate-400" />
                </button>
                {showPeriodDropdown && (
                  <div className="absolute z-50 mt-1 w-full bg-[#0C2340] border border-slate-700/30 rounded-lg shadow-lg">
                    <button
                      onClick={() => {
                        setSelectedPeriod('week');
                        setShowPeriodDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-slate-700/50 transition-colors text-white ${
                        selectedPeriod === 'week' ? 'bg-slate-800/30 bg-slate-800/50' : ''
                      }`}
                    >
                      最近一週
                    </button>
                    <button
                      onClick={() => {
                        setSelectedPeriod('month');
                        setShowPeriodDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-slate-700/50 transition-colors text-white ${
                        selectedPeriod === 'month' ? 'bg-slate-800/30 bg-slate-800/50' : ''
                      }`}
                    >
                      最近一個月
                    </button>
                    <button
                      onClick={() => {
                        setSelectedPeriod('6months');
                        setShowPeriodDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-slate-700/50 transition-colors text-white ${
                        selectedPeriod === '6months' ? 'bg-slate-800/30 bg-slate-800/50' : ''
                      }`}
                    >
                      最近六個月
                    </button>
                    <button
                      onClick={() => {
                        setSelectedPeriod('year');
                        setShowPeriodDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-slate-700/50 transition-colors text-white ${
                        selectedPeriod === 'year' ? 'bg-slate-800/30 bg-slate-800/50' : ''
                      }`}
                    >
                      最近一年
                    </button>
                  </div>
                )}
              </div>

              {/* Granularity Selector */}
              <div className="relative" ref={granularityDropdownRef}>
                <button
                  onClick={() => setShowGranularityDropdown(!showGranularityDropdown)}
                  className="px-4 py-2 border border-slate-700/50 rounded-lg bg-slate-800/50 text-white hover:bg-slate-700/30 hover:bg-slate-600 transition-colors flex items-center gap-2"
                >
                  <span>
                    {selectedGranularity === 'auto' ? 'AUTO' : selectedGranularity === 'minute' ? '分粒度' : selectedGranularity === 'hour' ? '時粒度' : '日粒度'}
                  </span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
                {showGranularityDropdown && (
                  <div className="absolute z-50 mt-1 w-full bg-[#0C2340] border border-slate-700/30 rounded-lg shadow-lg">
                    {(['auto', 'minute', 'hour', 'day'] as const).map((granularity) => {
                      const availableGranularities = getAvailableGranularities(selectedPeriod);
                      const isAvailable = availableGranularities.includes(granularity);
                      const label = granularity === 'auto' ? 'AUTO' : granularity === 'minute' ? '分粒度' : granularity === 'hour' ? '時粒度' : '日粒度';
                      
                      return (
                        <button
                          key={granularity}
                          onClick={() => {
                            if (isAvailable) {
                              setSelectedGranularity(granularity);
                              setShowGranularityDropdown(false);
                            }
                          }}
                          disabled={!isAvailable}
                          className={`w-full text-left px-4 py-2 transition-colors text-white ${
                            !isAvailable
                              ? 'opacity-50 cursor-not-allowed bg-slate-700/30 bg-[#0C2340]'
                              : 'hover:bg-slate-700/50'
                          } ${
                            selectedGranularity === granularity && isAvailable
                              ? 'bg-slate-800/30 bg-slate-800/50'
                              : ''
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateReport}
                disabled={!selectedDevice || isGenerating}
                className="px-6 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-500 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                    產生中...
                  </>
                ) : (
                  '產生報告'
                )}
              </button>
            </div>

            {/* Selected Device Info */}
            {selectedDevice && (
              <div className="mt-4 text-sm text-slate-400">
                已選擇: {selectedDevice.sn} ({getDeviceDisplayName(selectedDevice)}) {selectedDeviceType === '電力設備' ? '電力' : selectedDeviceType}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className={`mt-4 rounded-lg p-4 flex items-start gap-3 ${
                error.includes('Failed to fetch') || error.includes('網絡') || error.includes('連接')
                  ? 'bg-amber-900/20 border border-amber-200 border-amber-800'
                  : error.includes('沒有可用的數據')
                  ? 'bg-blue-900/20 border border-blue-200 border-blue-800'
                  : 'bg-red-900/20 border border-red-200 border-red-800'
              }`}>
                <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  error.includes('Failed to fetch') || error.includes('網絡') || error.includes('連接')
                    ? 'text-amber-500'
                    : error.includes('沒有可用的數據')
                    ? 'text-blue-500'
                    : 'text-red-500'
                }`} />
                <div className="flex-1">
                  <p className={`font-medium text-sm ${
                    error.includes('Failed to fetch') || error.includes('網絡') || error.includes('連接')
                      ? 'text-amber-800 text-amber-200'
                      : error.includes('沒有可用的數據')
                      ? 'text-blue-200'
                      : 'text-red-800 text-red-200'
                  }`}>
                    {error.includes('Failed to fetch') || error.includes('網絡') || error.includes('連接')
                      ? '網絡連接問題'
                      : error.includes('沒有可用的數據')
                      ? '暫無數據'
                      : '生成報告失敗'}
                  </p>
                  <p className={`text-xs mt-1 ${
                    error.includes('Failed to fetch') || error.includes('網絡') || error.includes('連接')
                      ? 'text-amber-600 text-amber-300'
                      : error.includes('沒有可用的數據')
                      ? 'text-blue-600 text-blue-300'
                      : 'text-red-600 text-red-300'
                  }`}>
                    {error.includes('Failed to fetch')
                      ? '無法連接到服務器，請檢查網絡連接或稍後再試。'
                      : error}
                  </p>
                  {(error.includes('Failed to fetch') || error.includes('網絡') || error.includes('連接')) && selectedDevice && (
                    <button
                      onClick={handleGenerateReport}
                      disabled={isGenerating}
                      className="mt-2 px-3 py-1.5 text-xs bg-amber-500 hover:bg-amber-600 disabled:bg-slate-500 text-white rounded-md transition-colors"
                    >
                      {isGenerating ? '重試中...' : '重試'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Data Analysis Report Section */}
        <div className="bg-[#0C2340] rounded-lg shadow-sm border border-slate-700/30 p-6">
          {/* Report Content */}
          <div className="flex-1 overflow-y-auto">
            {chartData.length > 0 ? (
              <>
                {/* Chart View - 包含標題和圖表的完整容器 */}
                {activeTab === 'chart' && (
                  <div ref={chartContainerRef}>
                    {/* Report Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <BarChart3 className="w-5 h-5 text-slate-400" />
                          <h3 className="text-lg font-semibold text-white">
                            數據分析報告
                          </h3>
                        </div>
                        {selectedDevice && (
                          <p className="text-sm text-slate-400">
                            {selectedDevice.sn}‧{getPeriodLabel(selectedPeriod)}‧{getDateRangeString(selectedPeriod)}
                          </p>
                        )}
                      </div>

                      {/* Download Button - 只在圖表頁顯示 */}
                      <div className="relative group">
                        <button className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700 rounded-lg transition-colors">
                          <Download className="w-4 h-4 text-orange-500" />
                          <span className="text-white">下載</span>
                          <ChevronDown className="w-4 h-4 text-white" />
                        </button>
                        <div className="absolute right-0 mt-1 w-48 bg-[#0C2340] border border-slate-700/30 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                          <button
                            onClick={handleDownloadPDF}
                            className="w-full text-left px-4 py-2 hover:bg-slate-700/50 text-sm text-white"
                          >
                            PDF
                          </button>
                          <button
                            onClick={handleDownloadPNG}
                            className="w-full text-left px-4 py-2 hover:bg-slate-700/50 text-sm text-white"
                          >
                            PNG
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-4 border-b border-slate-700/30 mb-8">
                      <button
                        onClick={() => setActiveTab('chart')}
                        className="px-4 py-2 font-medium transition-colors text-orange-500 border-b-2 border-orange-500"
                      >
                        圖表分析
                      </button>
                      <button
                        onClick={() => setActiveTab('table')}
                        className="px-4 py-2 font-medium transition-colors text-slate-400 hover:text-white hover:text-slate-200"
                      >
                        數據表格
                      </button>
                    </div>

                    {/* Chart */}
                    <div className="h-96">
                      {/* eslint-disable-next-line react/jsx-no-undef */}
                      <ReportChart 
                        data={chartData}
                        deviceType={selectedDeviceType}
                        showAvgPower={showAvgPower}
                        showEnergy={showEnergy}
                        showMinPower={showMinPower}
                        showPeakPower={showPeakPower}
                        onToggleEnergy={() => {
                          if (selectedDeviceType === '溫溼度') {
                            // 溫溼度：溫度和濕度一起顯示
                            const newState = !showEnergy;
                            setShowEnergy(newState);
                            setShowPeakPower(newState); // 濕度也一起切換
                          } else if (selectedDeviceType === '空氣品質') {
                            // 空氣品質：CO2 獨立切換
                            setShowEnergy(!showEnergy);
                          } else {
                            // 電力設備：用電量和三個功率互斥：選用電量時，隱藏三個功率
                            const newEnergyState = !showEnergy;
                            setShowEnergy(newEnergyState);
                            if (newEnergyState) {
                              setShowPeakPower(false);
                              setShowAvgPower(false);
                              setShowMinPower(false);
                            }
                          }
                        }}
                        onTogglePeakPower={() => {
                          if (selectedDeviceType === '溫溼度') {
                            // 溫溼度：溫度和濕度一起顯示
                            const newState = !showPeakPower;
                            setShowPeakPower(newState);
                            setShowEnergy(newState); // 溫度也一起切換
                          } else if (selectedDeviceType === '空氣品質') {
                            // 空氣品質：TVOC 獨立切換
                            setShowPeakPower(!showPeakPower);
                          } else {
                            // 電力設備：peak, avg, min 當成一組，一起切換；選功率時，隱藏用電量
                            const anyPowerVisible = showPeakPower || showAvgPower || showMinPower;
                            const newState = !anyPowerVisible;
                            setShowPeakPower(newState);
                            setShowAvgPower(newState);
                            setShowMinPower(newState);
                            if (newState) {
                              setShowEnergy(false);
                            }
                          }
                        }}
                        onToggleAvgPower={() => {
                          if (selectedDeviceType === '空氣品質') {
                            // 空氣品質：PM2.5 獨立切換
                            setShowAvgPower(!showAvgPower);
                          } else {
                            // 電力設備：peak, avg, min 當成一組，一起切換；選功率時，隱藏用電量
                            const anyPowerVisible = showPeakPower || showAvgPower || showMinPower;
                            const newState = !anyPowerVisible;
                            setShowPeakPower(newState);
                            setShowAvgPower(newState);
                            setShowMinPower(newState);
                            if (newState) {
                              setShowEnergy(false);
                            }
                          }
                        }}
                        onToggleMinPower={() => {
                          if (selectedDeviceType === '空氣品質') {
                            // 空氣品質：PM10 獨立切換
                            setShowMinPower(!showMinPower);
                          } else {
                            // 電力設備：peak, avg, min 當成一組，一起切換；選功率時，隱藏用電量
                            const anyPowerVisible = showPeakPower || showAvgPower || showMinPower;
                            const newState = !anyPowerVisible;
                            setShowPeakPower(newState);
                            setShowAvgPower(newState);
                            setShowMinPower(newState);
                            if (newState) {
                              setShowEnergy(false);
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Table View */}
                {activeTab === 'table' && (
                  <div ref={tableContainerRef}>
                    {/* Report Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <BarChart3 className="w-5 h-5 text-slate-400" />
                          <h3 className="text-lg font-semibold text-white">
                            數據分析報告
                          </h3>
                        </div>
                        {selectedDevice && (
                          <p className="text-sm text-slate-400">
                            {selectedDevice.sn}‧{getPeriodLabel(selectedPeriod)}‧{getDateRangeString(selectedPeriod)}
                          </p>
                        )}
                      </div>

                      {/* Download Button */}
                      <div className="relative group">
                        <button className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700 rounded-lg transition-colors">
                          <Download className="w-4 h-4 text-orange-500" />
                          <span className="text-white">下載</span>
                          <ChevronDown className="w-4 h-4 text-white" />
                        </button>
                        <div className="absolute right-0 mt-1 w-48 bg-[#0C2340] border border-slate-700/30 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                          <button
                            onClick={handleDownloadCSV}
                            className="w-full text-left px-4 py-2 hover:bg-slate-700/50 text-sm text-white"
                          >
                            CSV
                          </button>
                          <button
                            onClick={handleDownloadTablePDF}
                            className="w-full text-left px-4 py-2 hover:bg-slate-700/50 text-sm text-white"
                          >
                            PDF
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-4 border-b border-slate-700/30 mb-8">
                      <button
                        onClick={() => setActiveTab('chart')}
                        className="px-4 py-2 font-medium transition-colors text-slate-400 hover:text-white hover:text-slate-200"
                      >
                        圖表分析
                      </button>
                      <button
                        onClick={() => setActiveTab('table')}
                        className="px-4 py-2 font-medium transition-colors text-orange-500 border-b-2 border-orange-500"
                      >
                        數據表格
                      </button>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-700/30">
                            <th className="text-left py-3 px-4 font-medium text-slate-300">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // 如果當前已經是 time 字段，切換排序方向；否則設為降序
                                  let newOrder: 'asc' | 'desc';
                                  if (tableSort.field === 'time') {
                                    // 當前是 time 字段，切換方向
                                    newOrder = tableSort.order === 'asc' ? 'desc' : 'asc';
                                  } else {
                                    // 當前不是 time 字段，設為降序（最新的在前）
                                    newOrder = 'desc';
                                  }
                                  logger.log('🔄 Time sort clicked:', { 
                                    currentField: tableSort.field, 
                                    currentOrder: tableSort.order, 
                                    newOrder,
                                    willToggle: tableSort.field === 'time'
                                  });
                                  setTableSort({
                                    field: 'time',
                                    order: newOrder
                                  });
                                }}
                                className="flex items-center gap-2 hover:text-orange-500 transition-colors cursor-pointer"
                              >
                                <span>時間</span>
                                {tableSort.field === 'time' && (
                                  tableSort.order === 'asc' ? (
                                    <ArrowUp className="w-4 h-4 text-orange-500" />
                                  ) : (
                                    <ArrowDown className="w-4 h-4 text-orange-500" />
                                  )
                                )}
                              </button>
                            </th>
                            {selectedDeviceType === '電力設備' && (
                              <>
                                <th className="text-right py-3 px-4 font-medium text-slate-300">
                                  <button
                                    onClick={() => setTableSort({
                                      field: 'maxPower',
                                      order: tableSort.field === 'maxPower' && tableSort.order === 'asc' ? 'desc' : 'asc'
                                    })}
                                    className="flex items-center justify-end gap-2 w-full hover:text-orange-500 transition-colors"
                                  >
                                    <span>峰值功率 (W)</span>
                                    {tableSort.field === 'maxPower' && (
                                      tableSort.order === 'asc' ? (
                                        <ArrowUp className="w-4 h-4 text-orange-500" />
                                      ) : (
                                        <ArrowDown className="w-4 h-4 text-orange-500" />
                                      )
                                    )}
                                  </button>
                                </th>
                                <th className="text-right py-3 px-4 font-medium text-slate-300">
                                  <button
                                    onClick={() => setTableSort({
                                      field: 'avgPower',
                                      order: tableSort.field === 'avgPower' && tableSort.order === 'asc' ? 'desc' : 'asc'
                                    })}
                                    className="flex items-center justify-end gap-2 w-full hover:text-orange-500 transition-colors"
                                  >
                                    <span>平均功率 (W)</span>
                                    {tableSort.field === 'avgPower' && (
                                      tableSort.order === 'asc' ? (
                                        <ArrowUp className="w-4 h-4 text-orange-500" />
                                      ) : (
                                        <ArrowDown className="w-4 h-4 text-orange-500" />
                                      )
                                    )}
                                  </button>
                                </th>
                                <th className="text-right py-3 px-4 font-medium text-slate-300">
                                  <button
                                    onClick={() => setTableSort({
                                      field: 'minPower',
                                      order: tableSort.field === 'minPower' && tableSort.order === 'asc' ? 'desc' : 'asc'
                                    })}
                                    className="flex items-center justify-end gap-2 w-full hover:text-orange-500 transition-colors"
                                  >
                                    <span>最低功率 (W)</span>
                                    {tableSort.field === 'minPower' && (
                                      tableSort.order === 'asc' ? (
                                        <ArrowUp className="w-4 h-4 text-orange-500" />
                                      ) : (
                                        <ArrowDown className="w-4 h-4 text-orange-500" />
                                      )
                                    )}
                                  </button>
                                </th>
                                <th className="text-right py-3 px-4 font-medium text-slate-300">
                                  <button
                                    onClick={() => setTableSort({
                                      field: 'energy',
                                      order: tableSort.field === 'energy' && tableSort.order === 'asc' ? 'desc' : 'asc'
                                    })}
                                    className="flex items-center justify-end gap-2 w-full hover:text-orange-500 transition-colors"
                                  >
                                    <span>用電量 (kWh)</span>
                                    {tableSort.field === 'energy' && (
                                      tableSort.order === 'asc' ? (
                                        <ArrowUp className="w-4 h-4 text-orange-500" />
                                      ) : (
                                        <ArrowDown className="w-4 h-4 text-orange-500" />
                                      )
                                    )}
                                  </button>
                                </th>
                              </>
                            )}
                            {selectedDeviceType === '溫溼度' && (
                              <>
                                <th className="text-right py-3 px-4 font-medium text-slate-300">
                                  <button
                                    onClick={() => setTableSort({
                                      field: 'temperature',
                                      order: tableSort.field === 'temperature' && tableSort.order === 'asc' ? 'desc' : 'asc'
                                    })}
                                    className="flex items-center justify-end gap-2 w-full hover:text-orange-500 transition-colors"
                                  >
                                    <span>溫度 (°C)</span>
                                    {tableSort.field === 'temperature' && (
                                      tableSort.order === 'asc' ? (
                                        <ArrowUp className="w-4 h-4 text-orange-500" />
                                      ) : (
                                        <ArrowDown className="w-4 h-4 text-orange-500" />
                                      )
                                    )}
                                  </button>
                                </th>
                                <th className="text-right py-3 px-4 font-medium text-slate-300">
                                  <button
                                    onClick={() => setTableSort({
                                      field: 'humidity',
                                      order: tableSort.field === 'humidity' && tableSort.order === 'asc' ? 'desc' : 'asc'
                                    })}
                                    className="flex items-center justify-end gap-2 w-full hover:text-orange-500 transition-colors"
                                  >
                                    <span>濕度 (%)</span>
                                    {tableSort.field === 'humidity' && (
                                      tableSort.order === 'asc' ? (
                                        <ArrowUp className="w-4 h-4 text-orange-500" />
                                      ) : (
                                        <ArrowDown className="w-4 h-4 text-orange-500" />
                                      )
                                    )}
                                  </button>
                                </th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedTableData.map((point, index) => (
                            <tr key={index} className="border-b border-slate-700/30 hover:bg-slate-700/30 hover:bg-slate-700/50">
                              <td className="py-3 px-4 text-white">{point.displayTime}</td>
                              {selectedDeviceType === '電力設備' && (
                                <>
                                  <td className="py-3 px-4 text-right text-slate-300">
                                    {formatNumber(point.maxPower)}
                                  </td>
                                  <td className="py-3 px-4 text-right text-slate-300">
                                    {formatNumber(point.avgPower)}
                                  </td>
                                  <td className="py-3 px-4 text-right text-slate-300">
                                    {formatNumber(point.minPower)}
                                  </td>
                                  <td className="py-3 px-4 text-right text-slate-300 font-bold">
                                    {formatNumber(point.energy)}
                                  </td>
                                </>
                              )}
                              {selectedDeviceType === '溫溼度' && (
                                <>
                                  <td className="py-3 px-4 text-right text-slate-300">
                                    {formatNumber(point.temperature)}
                                  </td>
                                  <td className="py-3 px-4 text-right text-slate-300">
                                    {formatNumber(point.humidity)}
                                  </td>
                                </>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full py-20">
                <div className="text-center">
                  <BarChart3 className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                  <p className="text-lg text-slate-400">請選擇設備並產生報告</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(GenerateReportPage);

